/**
 *
 * @module customerServiceManager
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/customer-service-code.md
 *
 */

import Check from 'check-types';
import Logging from './../logging/entry';
import Services from './../services/entry';

const AccessToken = Services.Token.AccessToken;
const CustomerServiceManagerConfiguration = Services.Configuration.CustomerServiceManagerConfiguration;
const CustomerServiceClient = Services.CustomerService.CustomerServiceClient;

const InvalidArgumentException = Services.Exception.InvalidArgumentException;

const {
    instanceStrict
} = Check.assert;

/**
 *
 * @class CustomerServiceManager
 * @desc Provides a manager to be used to find a customer's current context.
 *
 */
export default class CustomerServiceManager {

    /**
     *
     * @param {SDK.Services.Configuration.CustomerServiceManagerConfiguration} config
     * @param {SDK.Services.CustomerService.CustomerServiceClient} customerServiceClient
     * @param {SDK.Logging.Logger} [logger=Logging]
     * @throws {InvalidArgumentException}
     *
     */
    constructor(config, customerServiceClient, logger = Logging) {

        const errorMessage = {
            ctor: 'CustomerServiceManager(config, customerServiceClient, logger)',
            get config() {
                return `${this.ctor} needs SDK.Services.Configuration.CustomerServiceManagerConfiguration instance`;
            },
            get customerServiceClient() {
                return `${this.ctor} needs SDK.Services.CustomerService.CustomerServiceClient instance`;
            }
        };

        instanceStrict(config, CustomerServiceManagerConfiguration, errorMessage.config, InvalidArgumentException);
        instanceStrict(customerServiceClient, CustomerServiceClient, errorMessage.customerServiceClient, InvalidArgumentException); // eslint-disable-line

        /**
         *
         * @access private
         * @type {SDK.Services.Configuration.CustomerServiceManagerConfiguration}
         *
         */
        this.config = config;

        /**
         *
         * @access private
         * @type {SDK.Services.CustomerService.CustomerServiceClient}
         *
         */
        this.client = customerServiceClient;

        /**
         *
         * @access private
         * @type {SDK.Logging.Logger}
         * @memberof {SDK.Logging.Logger}
         *
         */
        this.logger = logger;

        this.logger.log(CustomerServiceManager.name, 'Created.');

    }

    /**
     *
     * @access private
     * @param {SDK.Services.Token.AccessToken} accessToken - The current access token.
     * @desc Creates a customer service code for this user for use in support.
     * @returns {Promise<String>}
     *
     */
    async createSupportCode(accessToken) {

        const errorMessage = {
            method: 'CustomerServiceManager.createSupportCode(accessToken)',
            get accessToken() {
                return `${this.method} needs SDK.Services.Token.AccessToken instance`;
            }
        };

        instanceStrict(accessToken, AccessToken, errorMessage.accessToken, InvalidArgumentException);

        const { client, logger } = this;
        const customerSupportCode = await client.createSupportCode(accessToken);

        logger.info(CustomerServiceManager.name, 'Customer support code created');

        return Promise.resolve(customerSupportCode);

    }

}



// WEBPACK FOOTER //
// ./src/customerService/customerServiceManager.js